import { type UIMessage, useChat } from '@ai-sdk/react'
import { DefaultChatTransport, lastAssistantMessageIsCompleteWithToolCalls } from 'ai'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useCanvasStore } from '../../../stores/canvasStore'
import { useDesignStore } from '../../../stores/designStore'
import { useQueryFonts } from '../../hooks/useQueryFonts'
import type { Font } from '../../../domain/canvas/value-objects/font'
import type { ProductContext } from '../../../ai-agent/types'

// Collapse the flat Font catalog (one entry per weight/italic) into the AI hint shape:
// one entry per family name, with all weights deduped & sorted. Used so the agent only
// ever suggests fonts the design studio can actually render.
const buildFontHints = (fonts: Font[] | undefined): { name: string; weights: number[] }[] => {
  if (!fonts) return []
  const byName = new Map<string, Set<number>>()
  for (const f of fonts) {
    const existing = byName.get(f.name) ?? new Set<number>()
    existing.add(f.weight)
    byName.set(f.name, existing)
  }
  return Array.from(byName.entries())
    .map(([name, weights]) => ({ name, weights: Array.from(weights).sort((a, b) => a - b) }))
    .sort((a, b) => a.name.localeCompare(b.name))
}

// LocalStorage key prefix for chat history
const CHAT_STORAGE_PREFIX = 'design-studio-chat-'

// Get localStorage key for a specific design
const getChatStorageKey = (designId: string) => `${CHAT_STORAGE_PREFIX}${designId}`

// Load messages from localStorage
const loadMessagesFromStorage = (designId: string): UIMessage[] => {
  try {
    const stored = localStorage.getItem(getChatStorageKey(designId))
    if (stored) {
      return JSON.parse(stored)
    }
  } catch (error) {
    console.error('[AI Chat] Failed to load messages from storage:', error)
  }
  return []
}

// Save messages to localStorage
const saveMessagesToStorage = (designId: string, messages: UIMessage[]) => {
  try {
    localStorage.setItem(getChatStorageKey(designId), JSON.stringify(messages))
  } catch (error) {
    console.error('[AI Chat] Failed to save messages to storage:', error)
  }
}

// Asset from API (nested in folders)
type ApiAsset = {
  id: string
  filename: string
  thumbnail: string
  descriptions: {
    nl: string
    en: string
    fr: string
    de: string
  }
}

type ApiFolder = {
  id: string
  folderName: string
  translations: {
    nl: string
    en: string
    fr: string
    de: string
  }
  assets: ApiAsset[]
}

type ApiResponse = {
  engrave: ApiFolder[]
  print: ApiFolder[]
}

// Flattened asset for searching
type FlatAsset = {
  assetId: string
  folderId: string
  type: 'engrave' | 'print'
  filename: string
  folderName: string
  descriptions: {
    nl: string
    en: string
    fr: string
    de: string
  }
}

// Fetch and flatten asset context from API
const fetchAssetContext = async (): Promise<FlatAsset[]> => {
  const response = await fetch(
    'https://api.nicelittlethings.cloud/v3/design-studio/assets/ai-context',
  )
  if (!response.ok) {
    throw new Error(`Failed to fetch assets: ${response.status}`)
  }
  const data: ApiResponse = await response.json()

  // Flatten the nested structure
  const flatAssets: FlatAsset[] = []

  for (const folder of data.engrave || []) {
    for (const asset of folder.assets || []) {
      flatAssets.push({
        assetId: asset.id,
        folderId: folder.id,
        type: 'engrave',
        filename: asset.filename,
        folderName: folder.folderName,
        descriptions: asset.descriptions,
      })
    }
  }

  for (const folder of data.print || []) {
    for (const asset of folder.assets || []) {
      flatAssets.push({
        assetId: asset.id,
        folderId: folder.id,
        type: 'print',
        filename: asset.filename,
        folderName: folder.folderName,
        descriptions: asset.descriptions,
      })
    }
  }

  return flatAssets
}

// Tool execution results
type ToolResult = {
  success: boolean
  message: string
  data?: Record<string, unknown>
}

export const useDesignAgent = () => {
  const canvasStore = useCanvasStore()
  const designStore = useDesignStore()
  const availableFonts = useQueryFonts()

  // The transport body() is called at request time (outside React render), so it
  // needs a ref to read the latest font catalog without triggering re-creation.
  const fontHintsRef = useRef<{ name: string; weights: number[] }[]>([])
  useEffect(() => {
    fontHintsRef.current = buildFontHints(availableFonts)
  }, [availableFonts])

  // Build product context from stores
  const productContext: ProductContext | null = useMemo(() => {
    const design = designStore.currentDesign
    if (!design) return null

    return {
      productId: design.productId,
      variationId: design.variationId,
      designId: design.designId,
      type: design.type || 'print',
      baseColor: design.baseColor,
      designZone: {
        width: design.zones.design.width,
        height: design.zones.design.height,
      },
      fonts: buildFontHints(availableFonts),
    }
  }, [designStore.currentDesign, availableFonts])

  // Execute tools on the client side
  const executeToolCall = useCallback(
    async (toolName: string, args: Record<string, unknown>): Promise<ToolResult> => {
      const { elements } = canvasStore
      const design = designStore.currentDesign

      switch (toolName) {
        case 'getDesignInfo': {
          const elementList = Object.values(elements).map((el) => ({
            id: el.id,
            type: el.type,
            position: el.position,
            dimensions: el.dimensions,
            ...(el.type === 'text' && { content: el.content }),
            ...(el.type === 'asset' && { assetId: el.assetId }),
          }))

          return {
            success: true,
            message: `Design bevat ${elementList.length} element(en)`,
            data: {
              designId: design?.designId,
              type: design?.type,
              baseColor: design?.baseColor,
              designZone: {
                width: design?.zones.design.width,
                height: design?.zones.design.height,
              },
              elements: elementList,
            },
          }
        }

        case 'searchAssets': {
          const { query, type, limit } = args as {
            query: string
            type?: 'engrave' | 'print' | 'all'
            limit?: number
          }

          try {
            // Fetch flattened asset context from API
            const allAssets = await fetchAssetContext()

            // Filter by type if specified
            let filteredAssets = allAssets
            if (type && type !== 'all') {
              filteredAssets = allAssets.filter((asset) => asset.type === type)
            }

            // Search by descriptions (case-insensitive)
            const searchTerms = query.toLowerCase().split(/\s+/)
            const matchingAssets = filteredAssets.filter((asset) => {
              // Check all language descriptions
              const allText = [
                asset.descriptions?.nl || '',
                asset.descriptions?.en || '',
                asset.descriptions?.fr || '',
                asset.descriptions?.de || '',
                asset.filename || '',
                asset.folderName || '',
              ]
                .join(' ')
                .toLowerCase()

              // At least one search term must match
              return searchTerms.some((term) => allText.includes(term))
            })

            // Limit results
            const limitedAssets = matchingAssets.slice(0, limit || 10)

            const results = limitedAssets.map((asset) => ({
              assetId: asset.assetId,
              folderId: asset.folderId,
              type: asset.type,
              filename: asset.filename,
              folderName: asset.folderName,
              description: asset.descriptions?.nl || asset.descriptions?.en || '',
            }))

            return {
              success: true,
              message: `${results.length} assets gevonden voor "${query}"`,
              data: {
                query,
                results,
              },
            }
          } catch (error) {
            console.error('[AI Chat] Asset search error:', error)
            return {
              success: false,
              message: `Fout bij zoeken naar assets: ${error instanceof Error ? error.message : 'Onbekende fout'}`,
            }
          }
        }

        case 'addText': {
          const { content, x, y, fontSize, fontFamily, fontWeight, fontItalic, color } = args as {
            content: string
            x: number
            y: number
            fontSize?: number
            fontFamily?: string
            fontWeight?: number
            fontItalic?: boolean
            color?: string
          }

          await canvasStore.addTextElement(false)

          const allElements = Object.values(useCanvasStore.getState().elements)
          const newElement = allElements[allElements.length - 1]

          if (newElement && newElement.type === 'text') {
            const elementId = newElement.id

            await canvasStore.updateTextContent(elementId, content)
            await canvasStore.updateElementPosition(elementId, { x, y })

            if (fontFamily || fontWeight || fontItalic) {
              await canvasStore.updateTextFont(
                elementId,
                fontFamily || 'Arial',
                fontWeight || 400,
                fontItalic || false,
              )
            }

            if (fontSize) {
              await canvasStore.updateTextFontSize(elementId, fontSize)
            }

            if (color && design?.type === 'print') {
              await canvasStore.updateTextColor(elementId, color)
            }

            return {
              success: true,
              message: `Tekst "${content}" toegevoegd op positie (${x}, ${y})`,
              data: {
                elementId,
                position: { x, y },
                fontSize: fontSize || 8,
              },
            }
          }

          return {
            success: false,
            message: 'Kon tekst element niet toevoegen',
          }
        }

        case 'updateTextContent': {
          const { elementId, content } = args as {
            elementId: string
            content: string
          }

          canvasStore.updateTextContent(elementId, content)

          return {
            success: true,
            message: `Tekst gewijzigd naar "${content}"`,
            data: { elementId },
          }
        }

        case 'updateTextStyle': {
          const { elementId, fontSize, fontFamily, fontWeight, fontItalic, color } = args as {
            elementId: string
            fontSize?: number
            fontFamily?: string
            fontWeight?: number
            fontItalic?: boolean
            color?: string
          }

          if (fontSize) canvasStore.updateTextFontSize(elementId, fontSize)
          if (fontFamily || fontWeight !== undefined || fontItalic !== undefined) {
            canvasStore.updateTextFont(
              elementId,
              fontFamily || '',
              fontWeight ?? 400,
              fontItalic ?? false,
            )
          }
          if (color) canvasStore.updateTextColor(elementId, color)

          return {
            success: true,
            message: 'Tekst stijl gewijzigd',
            data: { elementId },
          }
        }

        case 'addAsset': {
          const { assetId, x, y, width, height } = args as {
            assetId: string
            x: number
            y: number
            width?: number
            height?: number
          }

          try {
            const { assetRegistry } = await import('../../../infrastructure/services/assetRegistry')
            const assetInfo = await assetRegistry.getAssetInfo(assetId)

            if (!assetInfo) {
              return {
                success: false,
                message: `Asset met ID ${assetId} niet gevonden`,
              }
            }

            await canvasStore.addAssetElement({
              src: assetInfo.url,
              name: assetInfo.fileName,
              assetId,
              assetType: assetInfo.type,
            })

            const allElements = Object.values(useCanvasStore.getState().elements)
            const newElement = allElements[allElements.length - 1]

            if (newElement) {
              if (x !== undefined && y !== undefined) {
                await canvasStore.updateElementPosition(newElement.id, { x, y })
              }

              if (width || height) {
                await canvasStore.updateElementSize(newElement.id, {
                  width: width || 15,
                  height: height || 15,
                })
              }

              return {
                success: true,
                message: `Asset "${assetInfo.fileName}" toegevoegd`,
                data: {
                  elementId: newElement.id,
                  assetId,
                  position: { x, y },
                },
              }
            }

            return {
              success: false,
              message: 'Asset toegevoegd maar element niet gevonden',
            }
          } catch (error) {
            return {
              success: false,
              message: `Fout bij toevoegen asset: ${error instanceof Error ? error.message : 'Onbekende fout'}`,
            }
          }
        }

        case 'moveElement': {
          const { elementId, x, y } = args as {
            elementId: string
            x: number
            y: number
          }

          canvasStore.updateElementPosition(elementId, { x, y })

          return {
            success: true,
            message: `Element verplaatst naar (${x}, ${y})`,
            data: { elementId, position: { x, y } },
          }
        }

        case 'resizeElement': {
          const { elementId, width, height } = args as {
            elementId: string
            width?: number
            height?: number
          }

          if (width || height) {
            canvasStore.updateElementSize(elementId, {
              width: width || 0,
              height: height || 0,
            })
          }

          return {
            success: true,
            message: 'Element grootte gewijzigd',
            data: { elementId, dimensions: { width, height } },
          }
        }

        case 'rotateElement': {
          const { elementId, rotation } = args as {
            elementId: string
            rotation: number
          }

          canvasStore.updateElementRotation(elementId, rotation)

          return {
            success: true,
            message: `Element geroteerd naar ${rotation}°`,
            data: { elementId, rotation },
          }
        }

        case 'centerElement': {
          const { elementId, axis } = args as {
            elementId: string
            axis: 'horizontal' | 'vertical' | 'both'
          }

          if (axis === 'horizontal' || axis === 'both') {
            canvasStore.centerElementHorizontally(elementId)
          }
          if (axis === 'vertical' || axis === 'both') {
            canvasStore.centerElementVertically(elementId)
          }

          return {
            success: true,
            message: `Element gecentreerd (${axis})`,
            data: { elementId, axis },
          }
        }

        case 'bringToFront': {
          const { elementId } = args as { elementId: string }
          canvasStore.bringToFront(elementId)

          return {
            success: true,
            message: 'Element naar voorgrond gebracht',
            data: { elementId },
          }
        }

        case 'sendToBack': {
          const { elementId } = args as { elementId: string }
          canvasStore.sendToBack(elementId)

          return {
            success: true,
            message: 'Element naar achtergrond gestuurd',
            data: { elementId },
          }
        }

        case 'deleteElement': {
          const { elementId } = args as { elementId: string }
          canvasStore.deleteElement(elementId)

          return {
            success: true,
            message: 'Element verwijderd',
            data: { elementId },
          }
        }

        case 'duplicateElement': {
          const { elementId } = args as { elementId: string }
          canvasStore.copyElement(elementId)
          canvasStore.pasteElement()

          return {
            success: true,
            message: 'Element gekopieerd',
            data: { originalId: elementId },
          }
        }

        case 'validateDesign': {
          const elementList = Object.values(elements)
          const designZone = design?.zones.design
          const outOfBounds: Array<{
            id: string
            type: string
            issue: string
            position: { x: number; y: number }
            dimensions: { width: number; height: number }
          }> = []

          if (designZone) {
            const zoneWidth = designZone.width
            const zoneHeight = designZone.height

            for (const el of elementList) {
              const { x } = el.position
              const { y } = el.position
              const w = el.dimensions.width
              const h = el.dimensions.height

              // Check if element extends beyond zone boundaries
              // Element is positioned at its center, so we need to account for half width/height
              const leftEdge = x - w / 2
              const rightEdge = x + w / 2
              const topEdge = y - h / 2
              const bottomEdge = y + h / 2

              const issues: string[] = []
              if (leftEdge < 0) issues.push(`links buiten (${leftEdge.toFixed(1)}mm)`)
              if (rightEdge > zoneWidth)
                issues.push(`rechts buiten (${(rightEdge - zoneWidth).toFixed(1)}mm over)`)
              if (topEdge < 0) issues.push(`boven buiten (${topEdge.toFixed(1)}mm)`)
              if (bottomEdge > zoneHeight)
                issues.push(`onder buiten (${(bottomEdge - zoneHeight).toFixed(1)}mm over)`)

              if (issues.length > 0) {
                outOfBounds.push({
                  id: el.id,
                  type: el.type,
                  issue: issues.join(', '),
                  position: { x, y },
                  dimensions: { width: w, height: h },
                })
              }
            }
          }

          const isValid = outOfBounds.length === 0

          return {
            success: true,
            message: isValid
              ? `Design OK: ${elementList.length} elementen binnen zone`
              : `PROBLEEM: ${outOfBounds.length} element(en) buiten zone`,
            data: {
              isValid,
              elementCount: elementList.length,
              designZone: designZone
                ? { width: designZone.width, height: designZone.height }
                : null,
              outOfBounds: outOfBounds.length > 0 ? outOfBounds : undefined,
            },
          }
        }

        default:
          return {
            success: false,
            message: `Onbekende tool: ${toolName}`,
          }
      }
    },
    [canvasStore, designStore],
  )

  // Create transport with product context in body (as function for dynamic evaluation)
  const transport = useMemo(() => {
    return new DefaultChatTransport({
      api: '/api/ai/chat',
      // Use a function to get current productContext at request time
      body: () => {
        const { currentDesign } = useDesignStore.getState()
        return {
          productContext: currentDesign
            ? {
                productId: currentDesign.productId,
                variationId: currentDesign.variationId,
                designId: currentDesign.designId,
                type: currentDesign.type || 'print',
                baseColor: currentDesign.baseColor,
                designZone: {
                  width: currentDesign.zones.design.width,
                  height: currentDesign.zones.design.height,
                },
                fonts: fontHintsRef.current,
              }
            : null,
          locale: 'nl',
        }
      },
    })
  }, [])

  // Track current designId for persistence
  const currentDesignId = designStore.currentDesign?.designId
  const previousDesignIdRef = useRef<string | undefined>(undefined)
  const isInitializedRef = useRef(false)

  // Use the AI SDK chat hook
  const { messages, status, error, setMessages, sendMessage, addToolResult } = useChat({
    transport,
    // Automatically send when all tool calls have results
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls,
    onToolCall: async ({ toolCall }) => {
      const result = await executeToolCall(
        toolCall.toolName,
        (toolCall as { input: Record<string, unknown> }).input,
      )

      // Only log validateDesign results for debugging positioning issues
      if (toolCall.toolName === 'validateDesign') {
        console.log('[AI Chat] validateDesign result:', result)
      }

      // Don't await addToolResult to avoid deadlocks (per AI SDK docs)
      addToolResult({
        toolCallId: toolCall.toolCallId,
        tool: toolCall.toolName,
        output: JSON.stringify(result),
      })
    },
  })

  // Load messages from localStorage when designId changes
  useEffect(() => {
    if (!currentDesignId) return

    // Only load if designId changed or on first mount
    if (previousDesignIdRef.current !== currentDesignId) {
      const storedMessages = loadMessagesFromStorage(currentDesignId)
      if (storedMessages.length > 0) {
        setMessages(storedMessages)
      }
      previousDesignIdRef.current = currentDesignId
      isInitializedRef.current = true
    }
  }, [currentDesignId, setMessages])

  // Save messages to localStorage when they change
  useEffect(() => {
    if (!currentDesignId || !isInitializedRef.current) return

    // Only save if we have messages (don't overwrite with empty on mount)
    if (messages.length > 0) {
      saveMessagesToStorage(currentDesignId, messages)
    }
  }, [messages, currentDesignId])

  // Derive isLoading from status
  const isLoading = status === 'submitted' || status === 'streaming'

  // Custom submit handler that checks for product context
  const handleSubmit = useCallback(
    async (content: string) => {
      if (!productContext) {
        console.error('No product context available')
        return
      }
      await sendMessage({ text: content })
    },
    [productContext, sendMessage],
  )

  // Clear chat history (also from localStorage)
  const clearChat = useCallback(() => {
    setMessages([])
    if (currentDesignId) {
      localStorage.removeItem(getChatStorageKey(currentDesignId))
    }
  }, [setMessages, currentDesignId])

  return {
    messages,
    handleSubmit,
    clearChat,
    isLoading,
    error,
    isReady: !!productContext,
  }
}
